import { useContext, useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthContext } from 'contexts/AuthContext'
import { AppContext } from 'contexts/AppContext'
import { Button, toast } from 'keep-react'
import {
  ArrowsLeftRight,
  Briefcase,
  CaretDown,
  CaretRight,
  SignOut,
  Storefront,
  User,
  Users,
} from 'phosphor-react'
import { RouteNamesAuth } from 'routes/enum'
import { productService } from 'api/services/product'
import { ProductCategory, ProductType } from 'api/types/product'
import { brokersMenu } from './brokersMenu'
import { backOfficeMenu } from './backOfficeMenu'

export interface MenuItem {
  label: string
  url: string
  icon?: () => JSX.Element
  submenu?: SubmenuCategory[] | null
  brokerMaster?: boolean
  access?: number[]
}

export interface SubmenuCategory {
  label: string | boolean
  items: SubmenuItem[]
}

export interface SubmenuItem {
  label: string
  url: string
  simulator?: boolean
  access?: number[]
}

export const SideBar = () => {
  const sidebarRef = useRef<HTMLDivElement | null>(null)
  const {
    setShowAccessMode,
    backOfficeMode,
    setBackOfficeMode,
    setProducts,
    setCategories,
  } = useContext(AppContext)
  const {
    accessLevel,
    currentBroker,
    setCurrentBroker,
    logout,
    roleId,
    currentBrokerLogo,
  } = useContext(AuthContext)
  const [activeMenu, setActiveMenu] = useState<number | null>(null)
  const [activeSubMenu, setActiveSubMenu] = useState<number | null>(null)
  const [adminMenu, _setAdminMenu] = useState<MenuItem[]>(backOfficeMenu)
  const [portalMenu, _setPortalMenu] = useState<MenuItem[]>(brokersMenu)
  const navigate = useNavigate()

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null
      if (
        sidebarRef.current &&
        target &&
        !sidebarRef.current.contains(target)
      ) {
        setActiveMenu(null)
      }
    }

    document.addEventListener('click', handleOutsideClick)
    document.addEventListener('touchstart', handleOutsideClick)

    return () => {
      document.removeEventListener('click', handleOutsideClick)
      document.removeEventListener('touchstart', handleOutsideClick)
    }
  }, [])

  const toggleMenu = (index: number) => {
    if (activeMenu === index) {
      setActiveMenu(null)
    } else {
      setActiveMenu(index)
    }
    setActiveSubMenu(null)
  }

  const toggleSubMenu = (index: number) => {
    if (activeSubMenu === index) {
      setActiveSubMenu(null)
    } else {
      setActiveSubMenu(index)
    }
  }

  const getCategories = async () => {
    try {
      const categories =
        (await productService.getCategories()) as ProductCategory[]

      categories.sort((a, b) => a.name.localeCompare(b.name))

      setCategories(categories)

      const products =
        (await productService.getPortalProductList()) as ProductType[]

      setProducts(products)

      const productSubMenu = categories.map(cat => {
        if (!cat.productList) {
          return {
            label: cat.name,
            items: [] as { label: string; url: string; simulator: boolean }[],
          }
        } else {
          return {
            label: cat.name,
            items: cat.productList
              .map(p => {
                const matched = products.find(
                  prod => prod.product === p.product && prod.status,
                )
                if (!matched) return null
                return {
                  label: matched.label ? matched.label : matched.description,
                  url: RouteNamesAuth.PRODUCT.replace(':slug', p.product),
                  simulator: matched.hasSimulation,
                }
              })
              .filter(
                (
                  it,
                ): it is { label: string; url: string; simulator: boolean } =>
                  it !== null,
              ),
          }
        }
      })

      productSubMenu.sort((a, b) => a.label.localeCompare(b.label))

      productSubMenu.forEach(cat => {
        cat.items.sort((a, b) => a.label.localeCompare(b.label))
      })

      const simulatorsSubMenu = categories.map(cat => {
        if (!cat.productList) {
          return {
            label: cat.name,
            items: [] as { label: string; url: string; simulator: boolean }[],
          }
        } else {
          return {
            label: cat.name,
            items: cat.productList
              .map(p => {
                const matched = products.find(
                  prod => prod.product === p.product && prod.status,
                )
                if (!matched || !matched.hasSimulation) return null
                return {
                  label: matched.label ? matched.label : matched.description,
                  url: RouteNamesAuth.SIMULATOR.replace(':slug', p.product),
                  simulator: matched.hasSimulation,
                }
              })
              .filter(
                (it): it is { label: string; url: string; simulator: true } =>
                  it !== null,
              ),
          }
        }
      })

      simulatorsSubMenu.sort((a, b) => a.label.localeCompare(b.label))

      simulatorsSubMenu.forEach(cat => {
        cat.items.sort((a, b) => a.label.localeCompare(b.label))
      })

      portalMenu[1].submenu = productSubMenu
      portalMenu[2].submenu = simulatorsSubMenu
        .map(cat => (cat.items.length > 0 ? cat : null))
        .filter(
          (cat): cat is (typeof simulatorsSubMenu)[number] => cat !== null,
        )
    } catch (error) {
      toast.error('Não foi possível carregar as categorias.')
    }
  }

  useEffect(() => {
    getCategories()
  }, [])

  if (accessLevel === undefined) {
    return null
  }

  return (
    <div ref={sidebarRef} className="h-full w-[250px]">
      {!backOfficeMode && (
        <div
          className={`h-full w-full ${
            currentBroker?.IsGroup ? 'bg-sidebarMaster' : 'bg-sidebar'
          } rounded-r-lg flex flex-col items-start justify-between py-4 px-2`}
        >
          <div className="h-sm:space-y-2 space-y-4 text-center w-full">
            {currentBrokerLogo && (
              <img
                className="w-full h-auto h-sm:max-h-20 object-cover"
                src={currentBrokerLogo}
                alt="Logo"
              />
            )}

            {!currentBrokerLogo && currentBroker && (
              <img
                className="w-auto h-auto max-w-full max-h-20 m-auto"
                src="/images/default_logo.png"
                alt="Logo"
              />
            )}

            <h2 className="font-bold text-body-4 text-center text-white">
              {currentBroker ? currentBroker.BrokerName : ''}
            </h2>
            {accessLevel === 4 && (
              <Button
                color="secondary"
                variant="softBg"
                size="sm"
                onClick={() => {
                  setCurrentBroker(null)
                  setShowAccessMode(true)
                }}
              >
                <ArrowsLeftRight size={14} />
                Mudar perfil
              </Button>
            )}

            <ul className="h-sm:pt-3 pt-5 space-y-1 w-full">
              {portalMenu.map(
                (
                  { icon: Icon, label, url, submenu, brokerMaster, access },
                  i: number,
                ) => {
                  if (currentBroker?.IsGroup && !brokerMaster) return null
                  if (accessLevel && access && !access.includes(accessLevel))
                    return null
                  if (!submenu) {
                    return (
                      <li
                        key={`portal${i}`}
                        onClick={() => navigate(url)}
                        className="cursor-pointer text-white text-body-4 flex items-center gap-3 hover:bg-white hover:text-metal-900 p-2 rounded-md"
                      >
                        {Icon ? <Icon /> : null}
                        {label}
                      </li>
                    )
                  } else {
                    return (
                      <li
                        key={`portal${i}`}
                        className="cursor-pointer relative z-20 "
                        onClick={() => {
                          toggleMenu(i)
                        }}
                      >
                        <div className="cursor-pointer text-white text-body-4 flex items-center justify-between gap-3 h-sm:py-1 py-2 px-2 rounded-md hover:bg-white hover:text-metal-900">
                          <div className="flex items-center gap-3">
                            {Icon ? <Icon /> : null}
                            {label}
                          </div>
                          <span
                            className={`${
                              activeMenu === i ? '-rotate-180' : ''
                            }`}
                          >
                            <CaretDown size={20} />
                          </span>
                        </div>

                        <ul
                          className={`border-l border-white/50 border-1 ml-3 w-[calc(100%-13px)] text-left ${
                            activeMenu === i ? 'block' : 'hidden'
                          }`}
                        >
                          {submenu && submenu.length > 1 ? (
                            <div className={`relative w-full`}>
                              {submenu.map((subCat, j) => (
                                <div
                                  key={`subcat${j}`}
                                  className="text-left w-full "
                                >
                                  {subCat.label && (
                                    <h3
                                      onClick={e => {
                                        e.stopPropagation()
                                        toggleSubMenu(j)
                                      }}
                                      className="cursor-pointer block text-body-4 text-white tracking-tight hover:bg-white hover:text-primary-600 py-1 px-2 rounded-md w-full ml-1 relative whitespace-nowrap"
                                    >
                                      {subCat.label}
                                      <span className="absolute right-2 top-1/2 -translate-y-1/2">
                                        <CaretRight size={14} />
                                      </span>
                                    </h3>
                                  )}
                                  <ul
                                    className={`absolute left-[calc(100%+9px)] top-0 z-10 px-2 py-1 rounded-r-md ${
                                      activeMenu === i && activeSubMenu === j
                                        ? 'block'
                                        : `hidden`
                                    } ${
                                      currentBroker?.IsGroup
                                        ? 'bg-sidebarMaster'
                                        : 'bg-sidebar'
                                    }`}
                                  >
                                    {subCat.items.map(
                                      (subitem: SubmenuItem, k: number) => {
                                        return (
                                          <li
                                            key={`subitem${k}`}
                                            onClick={() =>
                                              navigate(subitem.url)
                                            }
                                            className="text-body-4 text-white hover:bg-white hover:text-metal-900 p-2 rounded-md whitespace-nowrap"
                                          >
                                            {subitem.label}
                                          </li>
                                        )
                                      },
                                    )}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          ) : (
                            submenu.map(subCat => {
                              return subCat.items.map(
                                (subitem: SubmenuItem, k: number) => {
                                  if (
                                    !subitem.access?.includes(accessLevel || 0)
                                  )
                                    return null
                                  return (
                                    <li
                                      key={`subitem${k}`}
                                      onClick={() =>
                                        navigate(subitem.url as string)
                                      }
                                      className="cursor-pointer block text-body-4 text-white tracking-tight hover:bg-white hover:text-primary-600 py-1 px-2 rounded-md w-full ml-1 relative whitespace-nowrap"
                                    >
                                      {subitem.label}
                                    </li>
                                  )
                                },
                              )
                            })
                          )}
                        </ul>
                      </li>
                    )
                  }
                  return null
                },
              )}
            </ul>
          </div>
          <div className="border-t border-white w-full">
            <ul className="h-sm:pt-3 pt-5 space-y-1">
              {roleId === 1 && (
                <li
                  onClick={() => {
                    setBackOfficeMode(!backOfficeMode)
                    navigate(RouteNamesAuth.BACKOFFICE_DASHBOARD)
                  }}
                  className="cursor-pointer text-white text-body-4 flex items-center gap-3 hover:bg-white hover:text-metal-900 h-sm:py-1 py-2 px-2 rounded-md"
                >
                  <Briefcase size={16} />
                  BackOffice
                </li>
              )}

              <li
                onClick={() => navigate(RouteNamesAuth.PROFILE)}
                className="cursor-pointer text-white text-body-4 flex items-center gap-3 hover:bg-white hover:text-metal-900 h-sm:py-1 py-2 px-2 rounded-md"
              >
                <User size={16} />
                Perfil
              </li>

              {!currentBroker?.IsGroup && accessLevel && accessLevel >= 3 ? (
                <li
                  onClick={() => navigate(RouteNamesAuth.PROFILE_BROKER)}
                  className="cursor-pointer text-white text-body-4 flex items-center gap-3 hover:bg-white hover:text-metal-900 h-sm:py-1 py-2 px-2 rounded-md"
                >
                  <Users size={16} />
                  Gerir contas
                </li>
              ) : (
                <></>
              )}
              <li
                onClick={() => logout()}
                className="text-white text-body-4 flex items-center gap-3 hover:bg-white hover:text-metal-900 h-sm:py-1 py-2 px-2 rounded-md"
              >
                <SignOut size={16} />
                Sair
              </li>
            </ul>
          </div>
        </div>
      )}

      {backOfficeMode && (
        <div className="h-full bg-sidebarAdmin rounded-r-lg flex flex-col items-start justify-between p-4">
          <ul className="pt-5 space-y-2">
            {adminMenu.map(({ icon: Icon, label, url }, i: number) => (
              <li
                key={`admin${i}`}
                onClick={() => navigate(url)}
                className="cursor-pointer text-white text-body-4 flex items-center gap-3 hover:bg-white hover:text-metal-900 h-sm:py-1 py-2 px-2 rounded-md"
              >
                {Icon ? <Icon /> : null}
                {label}
              </li>
            ))}
          </ul>
          <ul className="pt-5 space-y-2 border-t border-white w-full">
            <li
              onClick={() => {
                setBackOfficeMode(!backOfficeMode)
                navigate(RouteNamesAuth.DASHBOARD)
              }}
              className="cursor-pointer text-white text-body-4 flex items-center gap-3 hover:bg-white hover:text-metal-900 h-sm:py-1 py-2 px-2 rounded-md"
            >
              <Storefront size={16} />
              Portal
            </li>
            <li
              onClick={() => logout()}
              className="cursor-pointer text-white text-body-4 flex items-center gap-3 hover:bg-white hover:text-metal-900 h-sm:py-1 py-2 px-2 rounded-md"
            >
              <SignOut size={16} />
              Sair
            </li>
          </ul>
        </div>
      )}
    </div>
  )
}
